/* eslint-disable max-params */
import deepmerge from 'deepmerge';
import { i18n } from '../i18n';
import { createRequestDeduper } from './requestDeduper';

const isPlainObject = (val: unknown): boolean =>
  val !== null && typeof val === 'object' && Object.getPrototypeOf(val) === Object.prototype;
const runWithLoadDataDeduping = createRequestDeduper();

// headers is a fetch Headers instance OR a plain option-object header map — structural any per PLAYBOOK
const getHeaderValue = (headers: any, headerName: string): string => {
  if (!headers) return '';

  const normalizedName = headerName.toLowerCase();

  if (typeof headers.get === 'function') {
    return headers.get(headerName) || '';
  }

  const matchedHeaderKey = Object.keys(headers).find(key => key.toLowerCase() === normalizedName);
  return matchedHeaderKey ? headers[matchedHeaderKey] : '';
};

export const deepmergeOptions = { isMergeableObject: isPlainObject };

// options is a fetch RequestInit-superset merged via deepmerge — any per PLAYBOOK
// `options`/return stay `any`, endpoint accepts URL: exported public signatures (frozen per
// TS-P6-B00.3c) — match the pre-merge stack surface exactly
export const loadData = async function (
  endpoint: string | URL,
  options: any,
  normalize = true,
  rethrow = true,
  noHeaders = false
): Promise<any> {
  const defaultOptions = {
    method: 'GET',
    headers: noHeaders
      ? {}
      : {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          ...(i18n.language ? { 'Accept-Language': i18n.language } : {}),
        },
    credentials: 'include',
  };
  options = deepmerge(defaultOptions, options, deepmergeOptions);

  const method = (options.method || 'GET').toUpperCase();

  const executeRequest = async () => {
    let response;
    try {
      response = await fetch(endpoint, options);
    } catch (e) {
      throw new Error('Request was cancelled.', { cause: e });
    }

    if (rethrow && response.status >= 400) {
      const error: Error & { response?: Response } = new Error(
        `${response.status}: ${response.statusText} received from ${endpoint}`
      );
      error.response = response;
      throw error;
    }
    // Response OK (No-Content) - return response without serialization
    if (response.status === 204) {
      return response;
    }

    try {
      const json = await response.json();

      if (normalize) {
        const data = json.data ?? json;
        const normalizeObject = (val: any) => Object.assign({ id: val.id, type: val.type }, val.attributes);
        return Array.isArray(data) ? data.map(normalizeObject) : normalizeObject(data);
      }

      // This data should already be normalized. No need to normalize normalized data.
      return json;
    } catch (e) {
      // response must not be json, throw an object that matches redux-json-api errors
      throw { response };
    }
  };

  const shouldDedupe = method === 'GET' && !options.body;
  if (!shouldDedupe) {
    return await executeRequest();
  }

  const requestKey = [
    method,
    endpoint,
    normalize ? '1' : '0',
    rethrow ? '1' : '0',
    noHeaders ? '1' : '0',
    options.credentials || '',
    getHeaderValue(options.headers, 'Authorization') || '',
    getHeaderValue(options.headers, 'X-CSRF-Token') || '',
    getHeaderValue(options.headers, 'Accept-Language') || '',
  ].join('|');

  return await runWithLoadDataDeduping({
    scopeKey: loadData,
    requestKey,
    runRequest: executeRequest,
  });
};

export const loadApiData = async function (endpoint: string | URL, normalize?: boolean): Promise<any> {
  return await requestApiData(endpoint, normalize);
};

export const loadExternalData = async function (
  endpoint: string | URL,
  authToken?: any,
  normalize?: boolean
): Promise<any> {
  return await requestExternalData(endpoint, authToken, normalize);
};

export const requestApiData = async function (
  endpoint: string | URL,
  normalize?: boolean,
  method = 'GET',
  options: any = {},
  rethrow = true
): Promise<any> {
  const tokenMeta = document.querySelector('meta[name=csrf-token]');
  // NamedNodeMap member access — structural cast per the original conversion
  const csrfToken = tokenMeta ? (tokenMeta.attributes as any).content.value : '';

  const defaultOptions = {
    method,
    headers: {
      'X-CSRF-Token': csrfToken,
    },
    credentials: 'include',
  };
  options = deepmerge(defaultOptions, options, deepmergeOptions);

  return await loadData(endpoint, options, normalize, rethrow);
};

export const requestExternalData = async function (
  endpoint: string | URL,
  authToken?: any,
  normalize?: boolean,
  method = 'GET',
  options: any = {},
  rethrow = true,
  noHeaders = false
): Promise<any> {
  const defaultOptions = {
    method,
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  };
  options = deepmerge(defaultOptions, options, deepmergeOptions);

  return await loadData(endpoint, options, normalize, rethrow, noHeaders);
};
